import { useIsLiveOfflineRemoteSession } from '@/hooks/desktop/useIsLiveOfflineRemoteSession';
import { useIsRemoteReconnectedSession } from '@/hooks/desktop/useIsRemoteReconnectedSession';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { BluetoothOff } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

interface RemoteOfflineSessionIconProps {
  sessionId: string | null | undefined;
}

/**
 * Icon + tooltip while the session is held live-offline on the Remote device,
 * OR while it is the reconnected recording pending a manual end
 * (both flows: live-recovery reconnect and pure-offline connect). Mirrors the
 * DataTable's `RemoteOfflineSessionCell` (same hooks, same precedence) at the
 * sidebar's icon size.
 */
export function RemoteOfflineSessionIcon({ sessionId }: RemoteOfflineSessionIconProps) {
  const isLiveOffline = useIsLiveOfflineRemoteSession(sessionId);
  const isReconnectedPendingEnd = useIsRemoteReconnectedSession(sessionId);

  if (!isLiveOffline && !isReconnectedPendingEnd) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex shrink-0 items-center"
            data-testid="sidebar-remote-offline-session-icon"
          >
            <BluetoothOff className="size-3 text-text-secondary" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <FormattedMessage
            defaultMessage="Transcribing offline on Remote"
            id="Iaot4HYNZx"
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
